import { useRef } from 'react';
import {
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
} from '@ionic/react';

interface SwipeToReadProps {
  /** Marks the wrapped activity read. Fired by a full swipe or by tapping the
   *  revealed option. */
  onCommit: () => void;
  /** Label on the revealed option. Callers pass the translated string. */
  label: string;
  /** Cards that aren't dismissible yet (an order still awaiting action) pass
   *  true, so swipe can't clear something the buttons refuse to clear. */
  disabled?: boolean;
  /** Match the wrapped card's own corner radius so the revealed green panel
   *  doesn't peek past it. `lg` for the plain activity cards, `xl` for OrderCard. */
  radius?: 'lg' | 'xl';
  children: React.ReactNode;
}

/**
 * Swipe-left-to-mark-read wrapper around an activity card.
 *
 * Built on Ionic's IonItemSliding rather than a hand-rolled pointer gesture:
 * the list lives inside an IonModal sheet that already owns a vertical drag,
 * and Ionic's gesture controller arbitrates its own horizontal gesture against
 * that. A custom handler would have to re-implement that arbitration.
 *
 * The cards are plain divs, so IonItem here is a gesture host and nothing else
 * — every bit of its chrome (background, padding, inner border, min-height) is
 * zeroed out in global.scss under `.hp-activity-swipe`. The card inside keeps
 * its own border, padding and click handlers untouched.
 */
export const SwipeToRead: React.FC<SwipeToReadProps> = ({
  onCommit,
  label,
  disabled,
  radius = 'lg',
  children,
}) => {
  const slidingRef = useRef<HTMLIonItemSlidingElement | null>(null);

  // Close before committing. A full swipe (onIonSwipe) auto-closes, but a tap
  // on the option does not — and the parent's reload can rerender this row in
  // place, which would leave it stuck open behind a different activity.
  const commit = () => {
    slidingRef.current?.closeOpened();
    onCommit();
  };

  if (disabled) return <>{children}</>;

  return (
    <IonItemSliding
      ref={slidingRef}
      className={`hp-activity-swipe hp-activity-swipe--${radius}`}
    >
      <IonItem lines="none" className="hp-activity-swipe__item">
        {children}
      </IonItem>
      <IonItemOptions side="end" onIonSwipe={commit}>
        {/* `expandable` makes a swipe past the option's width commit outright,
            so the common case is one gesture with no second tap. */}
        <IonItemOption
          expandable
          color="success"
          onClick={commit}
          aria-label={label}
        >
          {label}
        </IonItemOption>
      </IonItemOptions>
    </IonItemSliding>
  );
};
